import { useState } from 'react'
import { Plus, Settings2, Play, Square } from 'lucide-react'
import { Header } from '@/components/layout/Header'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ExchangeBadge, GlobalIgnoreList, StatsCard } from '@/components/common'
import { OIAlertEditSheet } from '@/components/alerts/OIAlertEditSheet'
import {
  useOIAlertsStatus,
  useSaveOIAlert,
  useDeleteOIAlert,
  useUpdateOIAlertsIgnore,
  useStartOIAlerts,
  useStopOIAlerts,
} from '@/hooks/useAlerts'
import { useAuth } from '@/hooks/useAuth'
import type { Alert } from '@/types'
import { formatOIThreshold } from '@/lib/oiThreshold'

export default function Alerts() {
  const { data: status, isLoading } = useOIAlertsStatus()
  const saveAlert = useSaveOIAlert()
  const deleteAlert = useDeleteOIAlert()
  const updateIgnore = useUpdateOIAlertsIgnore()
  const startAlerts = useStartOIAlerts()
  const stopAlerts = useStopOIAlerts()
  const { isAdmin } = useAuth()

  const [editingAlert, setEditingAlert] = useState<Alert | null>(null)
  const [isSheetOpen, setIsSheetOpen] = useState(false)

  const alerts = status?.alerts || []
  const isRunning = status?.running ?? true

  const handleAddAlert = () => {
    setEditingAlert(null)
    setIsSheetOpen(true)
  }

  const handleEditAlert = (alert: Alert) => {
    setEditingAlert(alert)
    setIsSheetOpen(true)
  }

  const handleSaveAlert = async (alertData: Partial<Alert>) => {
    await saveAlert.mutateAsync(alertData)
  }

  const handleDeleteAlert = async (id: string) => {
    await deleteAlert.mutateAsync(id)
  }

  const handleUpdateIgnore = async (symbols: string[]) => {
    await updateIgnore.mutateAsync(symbols)
  }

  return (
    <div className="flex flex-col">
      <Header title="OI" />

      <div className="flex-1 space-y-6 p-6">
        {/* Header with Add Alert and Global Ignore */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge variant={isRunning ? 'success' : 'secondary'}>
              {isRunning ? 'Active' : 'Stopped'}
            </Badge>
            {isAdmin && (
              isRunning ? (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => stopAlerts.mutate()}
                  disabled={stopAlerts.isPending}
                >
                  <Square className="mr-2 h-3 w-3" />
                  {stopAlerts.isPending ? 'Stopping...' : 'Stop'}
                </Button>
              ) : (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => startAlerts.mutate()}
                  disabled={startAlerts.isPending}
                >
                  <Play className="mr-2 h-3 w-3" />
                  {startAlerts.isPending ? 'Starting...' : 'Start'}
                </Button>
              )
            )}
          </div>
          <div className="flex gap-2">
            <GlobalIgnoreList
              symbols={status?.ignore || []}
              alertType="OI"
              onUpdate={handleUpdateIgnore}
              isUpdating={updateIgnore.isPending}
              compact
            />
            <Button variant="outline" onClick={handleAddAlert}>
              <Plus className="mr-2 h-4 w-4" />
              Add Alert
            </Button>
          </div>
        </div>

        {/* Info Banner */}
        <Card className="border-blue-500/30 bg-blue-500/10">
          <CardContent className="py-4">
            <p className="text-sm text-blue-300">
              <strong>What are Open Interest alerts?</strong> Monitors Open Interest changes
              across exchanges and alerts when OI spikes or drops beyond threshold.
              Useful for detecting big money moves and significant position changes.
            </p>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-3">
          <StatsCard
            title="Status"
            value={isRunning ? 'Monitoring' : 'Stopped'}
            description="Open Interest monitoring"
            status={isRunning ? 'running' : 'stopped'}
          />
          <StatsCard
            title="Active Triggers"
            value={status?.triggers_count || 0}
            description="OI alerts triggered"
            status={isRunning ? 'running' : 'stopped'}
          />
          <StatsCard
            title="Configured Alerts"
            value={alerts.length}
            description="Active alert configurations"
          />
        </div>

        <div className="grid gap-6">
          {/* Configured Alerts */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Configured Alerts</CardTitle>
              <CardDescription>
                {alerts.length} alert{alerts.length !== 1 ? 's' : ''} configured
              </CardDescription>
            </CardHeader>
            <CardContent>
              {alerts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No alerts configured. Click "Add Alert" to create one.
                </div>
              ) : (
                <div className="space-y-3">
                  {alerts.map((alert) => (
                    <div
                      key={alert.id}
                      className="flex items-center justify-between rounded-lg border p-4"
                    >
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{alert.name || alert.id}</span>
                          <Badge variant={alert.enabled ? 'success' : 'secondary'}>
                            {alert.enabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {(alert.exchanges || [alert.exchange]).map((ex) => (
                            <ExchangeBadge key={ex} exchange={ex} />
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          OI {alert.oi_direction === 'increase' ? 'increase' : alert.oi_direction === 'decrease' ? 'decrease' : 'change'} ≥ {formatOIThreshold(alert)} | Period: {alert.oi_period} |
                          Vol: {alert.filters?.min_period_volume_usdt || 0} USDT
                        </p>
                        {/* Tags */}
                        {((alert.required_tags?.length || 0) > 0 || (alert.exclude_tags?.length || 0) > 0) && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {(alert.required_tags || []).map((tag) => (
                              <Badge key={tag} className="bg-green-500/20 text-green-500 text-xs">
                                +{tag}
                              </Badge>
                            ))}
                            {(alert.exclude_tags || []).map((tag) => (
                              <Badge key={tag} variant="destructive" className="text-xs">
                                -{tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditAlert(alert)}
                        >
                          <Settings2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit Sheet */}
      <OIAlertEditSheet
        open={isSheetOpen}
        onOpenChange={setIsSheetOpen}
        alert={editingAlert}
        onSave={handleSaveAlert}
        onDelete={handleDeleteAlert}
      />
    </div>
  )
}
